<template>
  <v-text-field
      :error-messages="error"
      :label="translation(translationList.vehicle_number_of_seats, 'Număr locuri')"
      v-model="numberOfSeatsString"
      name="number_of_seats"
      :rules="numberOfSeatsValidationRules"
      @input="onInput"
      variant="outlined"
      :tile="true"
      type="number"
      :readonly="isReadonly"
      :disabled="isReadonly"
  >
    <template v-slot:append-inner>
      <v-tooltip location="top" open-delay="500" open-on-click open-on-hover>
        <template v-slot:activator="{ props }">
           <v-icon v-bind="props" class="cursor-pointer" aria-label="info">mdi-information</v-icon>
        </template>
        <span>
          <NuxtImg
              src="/images/vehicle/registration_template/template_s1.png"
              class="h-100"
              loading="lazy"
              alt="Registration template"
          />
        </span>
      </v-tooltip>
    </template>
  </v-text-field>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, toRefs } from 'vue';
import { useTranslationStore, translation } from '~/plugins/store/translations';
import type {TranslationList} from "~/types/Utils";

export default defineComponent({
  props: {
    modelValue: Number as () => number | null,
    isReadonly: Boolean as () => boolean | null,
  },
  setup(props, { emit }) {
    const { modelValue } = toRefs(props);
    const numberOfSeats = ref<number | null>(modelValue.value ?? -1);
    const numberOfSeatsString = ref<string | null>(modelValue.value !== null ? String(modelValue.value) : null);
    const error = ref<string[]>([]);

    watch(numberOfSeats, (newValue) => {
      const isValid = validateNumberOfSeats(newValue);
      if (isValid) {
        emit('update:modelValue', newValue);
      } else {
        emit('update:modelValue', null);
      }
      emit('validation-vehicle-status', { field: 'number_of_seats', isValid });
    });

    watch(numberOfSeatsString, (newValue) => {
      const parsedValue = parseFloat(newValue as string);
      if (!isNaN(parsedValue)) {
        numberOfSeats.value = parsedValue;
      } else {
        numberOfSeats.value = null;
      }
    });

    function validateNumberOfSeats(value: number | null, showErrors?: boolean): boolean {
      error.value = [];
      if (value !== null) {
        if (showErrors) {
          const errors = numberOfSeatsValidationRules
              .map(rule => rule(value))
              .filter(result => result !== true) as string[];
          if (errors.length > 0) {
            error.value = errors;
            return false;
          }
        }
        return numberOfSeatsValidationRules.every(rule => rule(value) === true);
      } else if (showErrors) {
        error.value.push(translation(translationList.value.required, 'Acest câmp este obligatoriu'));
        return false;
      }
      return false;
    }

    const translationList = ref<TranslationList>({});
    const translationStore = useTranslationStore();

    const numberOfSeatsValidationRules = [
      (v: number | null) => v !== null && v !== undefined && v > -1 || translation(translationList.value.required, 'Acest câmp este obligatoriu'),
      (v: number) => v >= 0 || translation(translationList.value.invalid_field, 'Acest câmp este obligatoriu'),
    ];

    const onInput = (event: Event) => {
      const input = event.target as HTMLInputElement;
      numberOfSeatsString.value = input.value;
    };

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');

      // Listen for validate event
      window.addEventListener('validate-vehicle', (data) => {
        if (data.detail && data.detail === 'number_of_seats') {
          const isValid = validateNumberOfSeats(numberOfSeats.value, true);
          if (isValid) {
            emit('validation-vehicle-status', { field: 'number_of_seats', isValid });
          }
        }
      });
    });

    watch(modelValue, (newValue) => {
      const formattedValue = newValue !== null ? String(newValue) : null;
      if (formattedValue !== numberOfSeatsString.value) {
        numberOfSeatsString.value = formattedValue;
      }
    });

    return {
      numberOfSeats,
      numberOfSeatsString,
      numberOfSeatsValidationRules,
      translationList,
      translation,
      onInput,
      error,
    };
  },
});
</script>
